import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Beaker, Factory, FileText, Brain, Package, ChevronDown, Check, Star, ArrowRight, Menu, X, Sparkles, ClipboardList, TrendingUp, MessageSquare, Zap, Lightbulb, Heart, AlertTriangle } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [contactForm, setContactForm] = useState({ name: '', email: '', subject: '', body: '' });
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm),
      });
      if (!res.ok) throw new Error();
      setContactSent(true);
      setContactForm({ name: '', email: '', subject: '', body: '' });
    } catch (e) {
      alert("Erreur lors de l'envoi. Réessayez ou écrivez à dippilote@outlook.com.");
    } finally { setContactSending(false); }
  };

  const assistantExamples = [
    { command: 'J\'ai fabriqué 20 baumes à lèvres aujourd\'hui', result: 'Il met à jour le journal de production et déduit automatiquement les matières premières du stock' },
    { command: 'Crée une nouvelle matière première avec le fournisseur X', result: 'C\'est fait instantanément, avec le prix et la catégorie' },
    { command: 'Voici mon stock actuel', result: 'Il l\'enregistre et le structure pour vous' },
    { command: 'Ajoute 20 baumes et 20 huiles au prévisionnel', result: 'Il ajuste vos prévisions et calcule automatiquement les besoins d\'achat' },
  ];

  const testimonials = [
    { name: 'Marie L.', role: 'Savonnière artisanale', text: 'DIP PILOTE +IA m\'a fait gagner des semaines de travail. Je gère ma prod et mes DIP au même endroit, c\'est un gain de temps incroyable.', stars: 5 },
    { name: 'Thomas D.', role: 'Fabricant de bougies parfumées', text: 'L\'assistant IA est bluffant. Je lui dis ce que j\'ai produit et il gère tout seul les stocks et le journal. Plus d\'erreurs !', stars: 5 },
    { name: 'Sophie R.', role: 'Laboratoire cosmétique', text: 'Le prévisionnel avec la liste d\'approvisionnement automatique me fait gagner 2h par semaine. Indispensable.', stars: 5 }
  ];

  const faqs = [
    { q: 'À qui s\'adresse DIP PILOTE +IA ?', a: 'Aux artisans savonniers, fabricants de bougies, créateurs de cosmétiques naturels et laboratoires. Que vous soyez en micro-entreprise ou en SARL, l\'outil s\'adapte à votre activité.' },
    { q: 'Qu\'est-ce qu\'un DIP ?', a: 'Le Dossier d\'Information Produit (DIP) est un document obligatoire selon le Règlement CE 1223/2009 pour tout produit cosmétique mis sur le marché européen. Il contient la formule, l\'évaluation de sécurité, les preuves d\'efficacité et les données du fabricant.' },
    { q: 'L\'offre gratuite est-elle vraiment gratuite ?', a: 'Oui, 100% gratuite et sans engagement. Vous avez accès à toutes les fonctionnalités avec une limite de 3 produits finis et 10 matières premières. Idéal pour tester et démarrer.' },
    { q: 'L\'assistant IA remplace-t-il un toxicologue ?', a: 'Non. L\'assistant vous aide à structurer votre DIP et votre production, mais l\'évaluation de sécurité doit toujours être validée par un toxicologue qualifié. Vous pouvez intégrer son rapport directement dans votre DIP.' },
    { q: 'Comment fonctionne le calcul automatique d\'approvisionnement ?', a: 'Vous définissez vos recettes et vos objectifs de production (prévisionnel). L\'outil compare les besoins totaux avec votre stock actuel et génère automatiquement la liste exacte de ce qu\'il faut commander, exportable en PDF ou CSV.' },
    { q: 'Puis-je exporter mes DIP en PDF ?', a: 'Oui, le DIP est généré en PDF structuré conforme CE 1223/2009, avec sommaire, parties A/B/C/D et toutes les annexes (FDS, étiquettes, certificats) automatiquement intégrées.' },
    { q: 'Comment fonctionne l\'abonnement Premium ?', a: 'Le Premium est proposé en 2 formules : Mensuel (25€/mois, sans engagement, annulable à tout moment) ou Annuel (250€/an, soit 2 mois offerts). Le paiement est sécurisé via Stripe. L\'abonnement annuel est une souscription unique non remboursable, valable 1 an à partir du paiement.' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="DIP PILOTE +IA" className="h-9 w-9 object-contain" />
              <span className="text-lg font-semibold text-[#205F79] tracking-tight">DIP PILOTE +IA</span>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <a href="#production" className="text-sm text-gray-600 hover:text-[#205F79] transition-colors">Production</a>
              <a href="#assistant" className="text-sm text-gray-600 hover:text-[#205F79] transition-colors">Assistant IA</a>
              <a href="#dip" className="text-sm text-gray-600 hover:text-[#205F79] transition-colors">DIP</a>
              <a href="#dna" className="text-sm text-gray-600 hover:text-[#205F79] transition-colors">Notre histoire</a>
              <a href="#pricing" className="text-sm text-gray-600 hover:text-[#205F79] transition-colors">Tarifs</a>
              <a href="#contact" className="text-sm text-gray-600 hover:text-[#205F79] transition-colors">Contact</a>
              <Button variant="outline" size="sm" onClick={() => navigate('/login')} data-testid="nav-login">Se connecter</Button>
              <Button size="sm" onClick={() => navigate('/login?tab=register')} data-testid="nav-signup" className="bg-[#205F79] hover:bg-[#184a5e]">Formule gratuite</Button>
            </div>
            <button className="md:hidden" onClick={() => setMobileMenu(!mobileMenu)}>
              {mobileMenu ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
          {mobileMenu && (
            <div className="md:hidden py-4 border-t border-gray-100 space-y-3">
              <a href="#production" className="block text-sm text-gray-600 py-1" onClick={() => setMobileMenu(false)}>Production</a>
              <a href="#assistant" className="block text-sm text-gray-600 py-1" onClick={() => setMobileMenu(false)}>Assistant IA</a>
              <a href="#pricing" className="block text-sm text-gray-600 py-1" onClick={() => setMobileMenu(false)}>Tarifs</a>
              <Button variant="outline" className="w-full" onClick={() => navigate('/login')}>Se connecter</Button>
              <Button className="w-full bg-[#205F79]" onClick={() => navigate('/login?tab=register')}>Formule gratuite</Button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-28 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#f0f7fa] to-white">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#205F79]/10 text-[#205F79] text-sm font-medium mb-8">
            <Zap size={14} /> Logiciel 2 en 1 : Production + DIP
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight tracking-tight">
            Optimisez toute votre<br />
            <span className="text-[#205F79]">production, sans effort.</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed">
            Pour tout laboratoire, artisan savonnier, cosmétique ou fabricant de bougies.
            Gérez vos stocks, recettes, coûts et DIP conformes depuis un seul outil intelligent.
            L'assistant IA vous fait gagner un temps incroyable — dites-lui, il fait pour vous.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-[#205F79] hover:bg-[#184a5e] text-base px-8 h-12 gap-2" data-testid="hero-cta" onClick={() => navigate('/login?tab=register')}>
              Commencer gratuitement <ArrowRight size={18} />
            </Button>
            <Button size="lg" variant="outline" className="text-base px-8 h-12 border-gray-300" onClick={() => document.getElementById('assistant').scrollIntoView({ behavior: 'smooth' })}>
              Voir l'assistant en action
            </Button>
          </div>
          <p className="mt-4 text-sm text-gray-500">Gratuit jusqu'à 3 produits — Aucune carte bancaire requise</p>
        </div>
      </section>

      {/* Production Section */}
      <section id="production" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-base font-semibold text-[#205F79] uppercase tracking-wider mb-3">Faites votre prod</h2>
            <p className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Tout est inclus, tout est automatisé</p>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Gérez facilement vos stocks de matières premières, vos recettes et vos coûts de production depuis un seul outil intelligent.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Package, title: 'Matières premières', desc: 'Intégrez toutes vos matières, suivez les prix d\'achat, fournisseurs et stock en temps réel.' },
              { icon: Beaker, title: 'Recettes & Coûts', desc: 'Créez vos recettes, le coût de revient et vos marges se calculent automatiquement.' },
              { icon: ClipboardList, title: 'Journal de production', desc: 'Enregistrez vos fabrications, les stocks sont déduits automatiquement.' },
              { icon: TrendingUp, title: 'Prévisionnel de prod', desc: 'Planifiez vos objectifs, l\'outil calcule les besoins en matières premières.' },
              { icon: Factory, title: 'Liste d\'approvisionnement', desc: 'Générée automatiquement en comparant votre stock actuel et vos besoins. Export PDF/CSV.' },
              { icon: Package, title: 'Inventaire & Suivi', desc: 'Visualisez votre stock en temps réel, recevez des alertes de stock bas.' }
            ].map((f, i) => (
              <div key={i} className="p-6 rounded-xl border border-gray-100 hover:border-[#205F79]/20 hover:shadow-lg transition-all duration-300 group">
                <div className="w-12 h-12 rounded-lg bg-[#205F79]/10 flex items-center justify-center mb-4 group-hover:bg-[#205F79]/15 transition-colors">
                  <f.icon className="text-[#205F79]" size={24} strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Assistant IA Section */}
      <section id="assistant" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#f8fafb]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-base font-semibold text-[#205F79] uppercase tracking-wider mb-3">Assistant IA</h2>
            <p className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Un assistant qui travaille pour vous</p>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Déclarez simplement vos actions, il s'occupe du reste. Plus qu'un outil, un véritable assistant opérationnel.</p>
          </div>

          <div className="space-y-4 max-w-3xl mx-auto">
            {assistantExamples.map((ex, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-8 h-8 rounded-full bg-[#205F79]/10 flex items-center justify-center mt-0.5">
                    <MessageSquare size={16} className="text-[#205F79]" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 mb-1.5">« {ex.command} »</p>
                    <p className="text-sm text-gray-600 flex items-start gap-2">
                      <ArrowRight size={14} className="text-[#205F79] shrink-0 mt-0.5" />
                      {ex.result}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="inline-block bg-white border border-[#205F79]/20 rounded-xl p-6 max-w-lg">
              <Sparkles className="text-[#205F79] mx-auto mb-3" size={28} />
              <p className="font-semibold text-gray-900 mb-2">Résultat</p>
              <p className="text-sm text-gray-600 leading-relaxed">
                Une liste d'approvisionnement précise, basée sur vos stocks réels et vos prévisions.
                <span className="block mt-2 font-medium text-[#205F79]">Gagnez du temps, réduisez les erreurs, pilotez votre production avec précision.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DIP Section */}
      <section id="dip" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#205F79]/10 text-[#205F79] text-sm font-medium mb-6">
              <Shield size={14} /> Conforme CE 1223/2009
            </div>
            <p className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Et ce n'est pas tout : vos DIP en quelques minutes</p>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Quand vous ajoutez un nouveau produit, créez votre DIP complet. L'assistant vous guide pour les BPF, la fabrication et les documents obligatoires.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { icon: FileText, title: 'DIP PDF structuré', desc: 'Généré automatiquement avec sommaire, parties A/B/C/D, conforme au Règlement européen.' },
              { icon: Brain, title: 'BPF assistées par l\'IA', desc: 'L\'assistant vous aide à rédiger les Bonnes Pratiques de Fabrication et les étapes de production.' },
              { icon: Shield, title: 'Documents obligatoires', desc: 'Intégrez l\'étude du toxicologue, FDS fournisseurs, certificats et étiquettes — tout dans le DIP.' },
              { icon: Beaker, title: 'INCI automatique', desc: 'Saisissez vos ingrédients en français, l\'IA traduit en nomenclature INCI normalisée.' },
            ].map((f, i) => (
              <div key={i} className="flex gap-4 p-5 rounded-xl border border-gray-100 hover:border-[#205F79]/20 hover:shadow-md transition-all">
                <div className="shrink-0 w-11 h-11 rounded-lg bg-[#205F79]/10 flex items-center justify-center">
                  <f.icon className="text-[#205F79]" size={22} strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">{f.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* L'ADN de DIP PILOTE — Notre histoire */}
      <section id="dna" className="py-24 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden">
        {/* Decorative bg */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#205F79]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-200/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

        <div className="max-w-5xl mx-auto relative">
          <div className="text-center mb-16">
            <h2 className="text-base font-semibold text-[#205F79] uppercase tracking-wider mb-3">Notre histoire</h2>
            <p className="text-3xl sm:text-4xl font-bold text-gray-900">L'ADN de DIP PILOTE</p>
            <div className="w-16 h-1 bg-[#D2691E] mx-auto mt-6 rounded-full" />
          </div>

          <div className="space-y-10">
            {/* Le Problème */}
            <div className="grid md:grid-cols-[auto_1fr] gap-6 items-start">
              <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="text-amber-700" size={26} strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-2">Le Problème</p>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Le « casse-tête » de l'artisan</h3>
                <p className="text-gray-700 leading-relaxed">
                  Tout commence en 2020. Ma femme lance sa marque de cosmétiques et son propre laboratoire.
                  En l'accompagnant, je découvre une réalité brutale : la gestion administrative est un gouffre
                  financier et mental. Entre les recettes sur papier, les calculs approximatifs et les stocks
                  gérés « au hasard », la passion laisse vite place à l'épuisement. Avec 10 produits finis et
                  40 matières premières, les tableaux Excel deviennent infinis et la traçabilité (lots, DIP, BPF)
                  une montagne infranchissable.
                </p>
              </div>
            </div>

            {/* La Mission */}
            <div className="grid md:grid-cols-[auto_1fr] gap-6 items-start">
              <div className="w-14 h-14 rounded-2xl bg-[#205F79]/10 flex items-center justify-center shrink-0">
                <Lightbulb className="text-[#205F79]" size={26} strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-xs font-semibold text-[#205F79] uppercase tracking-wider mb-2">La Mission</p>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">L'innovation par l'intelligence artificielle</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Électronicien de métier, j'ai cherché un logiciel simple, intuitif et complet.
                  Ne trouvant rien qui réponde aux besoins réels des petits laboratoires, j'ai décidé de le créer.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <span className="font-semibold text-[#205F79]">DIP PILOTE n'est pas un simple logiciel de gestion ;
                  c'est un assistant intelligent.</span>{' '}
                  Grâce à l'IA intégrée, l'outil automatise les tâches les plus lourdes : ajout de matières premières,
                  calcul précis des coûts de revient, prévisionnels de production et génération simplifiée du DIP.
                  Tout est relié, tout est fluide.
                </p>
              </div>
            </div>

            {/* L'Engagement */}
            <div className="grid md:grid-cols-[auto_1fr] gap-6 items-start">
              <div className="w-14 h-14 rounded-2xl bg-rose-100 flex items-center justify-center shrink-0">
                <Heart className="text-rose-600" size={26} strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-xs font-semibold text-rose-700 uppercase tracking-wider mb-2">L'Engagement</p>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Soutenir la création</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Parce que chaque grande marque a commencé avec peu de moyens, j'ai tenu à ce que DIP PILOTE soit
                  accessible.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Nous proposons une <span className="font-semibold">offre gratuite (jusqu'à 3 produits finis)</span>
                  {' '}pour permettre aux créateurs de démarrer sur des bases professionnelles sans coût initial.
                  Pour ceux qui veulent passer à la vitesse supérieure, notre version Premium offre la puissance d'un
                  assistant IA dédié à leur conformité.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" className="bg-[#205F79] hover:bg-[#184a5e] gap-2 h-12 px-8" onClick={() => navigate('/login?tab=register')} data-testid="dna-cta-register">
              Démarrer gratuitement <ArrowRight size={18} />
            </Button>
            <a href="#pricing" className="text-sm text-[#205F79] hover:underline">
              Découvrir nos formules →
            </a>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#f8fafb]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-base font-semibold text-[#205F79] uppercase tracking-wider mb-3">Tarifs</h2>
            <p className="text-3xl sm:text-4xl font-bold text-gray-900">Simple et transparent</p>

            {/* Billing toggle */}
            <div className="inline-flex items-center gap-1 mt-8 p-1 bg-white border border-gray-200 rounded-full shadow-sm" data-testid="billing-toggle">
              <button
                type="button"
                onClick={() => setBillingCycle('monthly')}
                className={`px-5 py-2 text-sm font-medium rounded-full transition-all ${billingCycle === 'monthly' ? 'bg-[#205F79] text-white shadow' : 'text-gray-600 hover:text-[#205F79]'}`}
                data-testid="billing-toggle-monthly"
              >
                Mensuel
              </button>
              <button
                type="button"
                onClick={() => setBillingCycle('annual')}
                className={`relative px-5 py-2 text-sm font-medium rounded-full transition-all ${billingCycle === 'annual' ? 'bg-[#205F79] text-white shadow' : 'text-gray-600 hover:text-[#205F79]'}`}
                data-testid="billing-toggle-annual"
              >
                Annuel
                <span className="absolute -top-2 -right-2 px-1.5 py-0.5 bg-amber-500 text-white text-[9px] font-bold rounded-full uppercase tracking-wide whitespace-nowrap">-2 mois</span>
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* Free */}
            <Card className="border-gray-200 hover:shadow-lg transition-shadow">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl">Gratuit</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-gray-900">0€</span>
                  <span className="text-gray-500 ml-1">/mois</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">Idéal pour démarrer et tester</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {['Toutes les fonctionnalités', 'Assistant IA inclus', 'DIP PDF conformes', 'Gestion de production', 'Export PDF & CSV', '3 produits finis max', '10 matières premières max'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                    <Check size={16} className={i >= 5 ? 'text-amber-500' : 'text-green-500'} />
                    <span className={i >= 5 ? 'text-amber-700 font-medium' : ''}>{item}</span>
                  </div>
                ))}
                <Button variant="outline" className="w-full mt-6" onClick={() => navigate('/login?tab=register')} data-testid="pricing-free-cta">
                  Commencer gratuitement
                </Button>
              </CardContent>
            </Card>

            {/* Premium */}
            <Card className="border-[#205F79] border-2 hover:shadow-xl transition-shadow relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-[#205F79] text-white text-xs font-semibold px-4 py-1 rounded-bl-lg">Recommandé</div>
              <CardHeader className="pb-4">
                <CardTitle className="text-xl">Premium</CardTitle>
                <div className="mt-4">
                  {billingCycle === 'monthly' ? (
                    <>
                      <span className="text-4xl font-bold text-[#205F79]" data-testid="pricing-premium-amount">25€</span>
                      <span className="text-gray-500 ml-1">/mois</span>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-[#205F79]" data-testid="pricing-premium-amount">250€</span>
                      <span className="text-gray-500 ml-1">/an</span>
                      <p className="text-xs text-amber-600 font-semibold mt-1">Soit 20,83€/mois — 2 mois offerts</p>
                    </>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-2">Pour les professionnels</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {['Toutes les fonctionnalités', 'Assistant IA inclus', 'DIP PDF conformes', 'Gestion de production', 'Export PDF & CSV', 'Produits illimités', 'Matières premières illimitées'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                    <Check size={16} className="text-green-500" />
                    <span className={i >= 5 ? 'font-semibold text-[#205F79]' : ''}>{item}</span>
                  </div>
                ))}
                <Button className="w-full mt-6 bg-[#205F79] hover:bg-[#184a5e]" onClick={() => navigate(`/login?tab=register&plan=${billingCycle}`)} data-testid="pricing-premium-cta">
                  {billingCycle === 'monthly' ? 'S\'abonner — 25€/mois' : 'S\'abonner — 250€/an'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-base font-semibold text-[#205F79] uppercase tracking-wider mb-3">Témoignages</h2>
            <p className="text-3xl sm:text-4xl font-bold text-gray-900">Ils nous font confiance</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <div key={i} className="p-6 rounded-xl bg-[#f8fafb] border border-gray-100">
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: t.stars }).map((_, j) => (
                    <Star key={j} size={16} className="fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed mb-4 italic">"{t.text}"</p>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{t.name}</p>
                  <p className="text-xs text-gray-500">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#f8fafb]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-base font-semibold text-[#205F79] uppercase tracking-wider mb-3">FAQ</h2>
            <p className="text-3xl sm:text-4xl font-bold text-gray-900">Questions fréquentes</p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-100 overflow-hidden">
                <button className="w-full text-left px-6 py-4 flex justify-between items-center hover:bg-gray-50 transition-colors"
                  data-testid={`faq-${i}`}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="text-sm font-medium text-gray-900 pr-4">{faq.q}</span>
                  <ChevronDown size={18} className={`text-gray-400 transition-transform shrink-0 ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4 text-sm text-gray-600 leading-relaxed">{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact form */}
      <section id="contact" className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-base font-semibold text-[#205F79] uppercase tracking-wider mb-3">Contact</h2>
            <p className="text-3xl sm:text-4xl font-bold text-gray-900">Une question, un projet ?</p>
            <p className="text-base text-gray-600 mt-3">Écrivez-nous, nous vous répondons sous 48h.</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
            {contactSent ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 rounded-full bg-green-100 mx-auto mb-4 flex items-center justify-center">
                  <Check size={28} className="text-green-600" />
                </div>
                <p className="text-xl font-semibold text-gray-900 mb-2">Message envoyé !</p>
                <p className="text-sm text-gray-600 mb-5">Merci, nous vous répondons rapidement à l'adresse indiquée.</p>
                <Button variant="outline" onClick={() => setContactSent(false)} data-testid="contact-send-another">
                  Envoyer un autre message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} className="space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5 block">Nom</label>
                    <input required type="text" value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="w-full px-3 h-10 rounded-md border border-gray-300 focus:border-[#205F79] focus:ring-2 focus:ring-[#205F79]/10 outline-none transition-all text-sm"
                      data-testid="contact-name" placeholder="Votre nom" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5 block">Email</label>
                    <input required type="email" value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="w-full px-3 h-10 rounded-md border border-gray-300 focus:border-[#205F79] focus:ring-2 focus:ring-[#205F79]/10 outline-none transition-all text-sm"
                      data-testid="contact-email" placeholder="vous@exemple.com" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5 block">Sujet</label>
                  <input required type="text" value={contactForm.subject}
                    onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                    className="w-full px-3 h-10 rounded-md border border-gray-300 focus:border-[#205F79] focus:ring-2 focus:ring-[#205F79]/10 outline-none transition-all text-sm"
                    data-testid="contact-subject" placeholder="Demande de démo, question, partenariat..." />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5 block">Message</label>
                  <textarea required rows={5} value={contactForm.body}
                    onChange={(e) => setContactForm({ ...contactForm, body: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-md border border-gray-300 focus:border-[#205F79] focus:ring-2 focus:ring-[#205F79]/10 outline-none transition-all text-sm resize-none"
                    data-testid="contact-body" placeholder="Votre message..." />
                </div>
                <p className="text-xs text-gray-500">
                  En envoyant ce message, vous acceptez que vos données soient utilisées pour vous répondre, conformément à
                  notre <a href="/legal/confidentialite" className="underline hover:no-underline">politique de confidentialité</a>.
                </p>
                <Button type="submit" size="lg"
                  className="w-full h-12 gap-2 bg-[#205F79] hover:bg-[#184a5e] text-base"
                  disabled={contactSending} data-testid="btn-contact-send">
                  {contactSending ? 'Envoi en cours...' : (<><MessageSquare size={18} /> Envoyer le message</>)}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#205F79]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Plus qu'un outil, un assistant opérationnel</h2>
          <p className="text-lg text-white/80 mb-8">Rejoignez les artisans qui pilotent leur production avec précision.</p>
          <Button size="lg" className="bg-white text-[#205F79] hover:bg-gray-100 text-base px-10 h-12 gap-2" onClick={() => navigate('/login?tab=register')} data-testid="cta-final">
            Créer mon compte gratuit <ArrowRight size={18} />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 pb-8 border-b border-gray-800">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="DIP PILOTE +IA" className="h-8 w-8 object-contain brightness-200" />
              <span className="text-sm text-gray-400">DIP PILOTE +IA — Production & DIP conformes CE 1223/2009</span>
            </div>
            <div className="flex gap-6 flex-wrap">
              <a href="#dna" className="text-sm text-gray-400 hover:text-white transition-colors">Notre histoire</a>
              <a href="#faq" className="text-sm text-gray-400 hover:text-white transition-colors">FAQ</a>
              <a href="#pricing" className="text-sm text-gray-400 hover:text-white transition-colors">Tarifs</a>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6">
            <p className="text-xs text-gray-500">© {new Date().getFullYear()} DIP PILOTE +IA. Tous droits réservés.</p>
            <div className="flex gap-5 flex-wrap text-xs">
              <button onClick={() => navigate('/legal/mentions')} className="text-gray-400 hover:text-white transition-colors" data-testid="footer-mentions">Mentions légales</button>
              <button onClick={() => navigate('/legal/cgu')} className="text-gray-400 hover:text-white transition-colors" data-testid="footer-cgu">CGU / CGV</button>
              <button onClick={() => navigate('/legal/confidentialite')} className="text-gray-400 hover:text-white transition-colors" data-testid="footer-rgpd">Confidentialité (RGPD)</button>
              <a href="mailto:dippilote@outlook.com" className="text-gray-400 hover:text-white transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
